import { type FC } from 'react'
import { type UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconArrowLeft, IconArrowRight } from '@redteclab/icons'

import { FormHelperText, IconButton } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormV3Values } from '../../../hooks/marketplacePartnerRegisterPageFormV3RHFModel'
import { MarketplacePartnerRegisterPageFormCategoriesSelectionCard } from '../MarketplacePartnerRegisterPageFormCategories/MarketplacePartnerRegisterPageFormCategoriesSelectionCard'

type CategorySelectionEntry = {
  isChecked: boolean
  isSelected: boolean
  key: string
  label: string
}

type MarketplacePartnerRegisterPageFormV3CategoriesSelectionProps = {
  entries: CategorySelectionEntry[]
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
  moveCategories: (toSelected: boolean) => void
  onCheck: (categoryKey: string, checked: boolean, isAvailable: boolean) => void
}

export const MarketplacePartnerRegisterPageFormV3CategoriesSelection: FC<
  MarketplacePartnerRegisterPageFormV3CategoriesSelectionProps
> = ({ entries, form, moveCategories, onCheck }) => {
  const intl = useIntl()
  const availableCategories = entries.filter((entry) => !entry.isSelected)
  const selectedCategories = entries.filter((entry) => entry.isSelected)

  return (
    <div className="flex flex-col items-center justify-center gap-8">
      {form.formState.errors.categories?.message ? (
        <FormHelperText
          data-qa-id="global-MessageForm-categories-error-msg"
          error
        >
          {form.formState.errors.categories.message}
        </FormHelperText>
      ) : null}
      <div className="flex flex-col items-center gap-8 tablet:flex-row">
        <MarketplacePartnerRegisterPageFormCategoriesSelectionCard
          data-qa-id="marketplacePartnerRegisterPage-card-availableCategories"
          entries={availableCategories.map(({ isChecked, key, label }) => ({
            isChecked,
            key,
            label,
          }))}
          id="marketplacePartnerRegisterPageCategoriesSelectionAvailable"
          label={intl.formatMessage({
            id: 'marketplacePartnerRegisterPage.categoriesSelection.available',
          })}
          onCheck={(value, checked) => {
            onCheck(value, checked, true)
          }}
        />
        <div className="flex gap-2 tablet:flex-col">
          <IconButton
            aria-label={intl.formatMessage({
              id: 'marketplacePartnerRegisterPage.categories.categoriesSelection.moveToSelected.title',
            })}
            data-qa-id="sellerForm-button-moveToSelected"
            disabled={entries.every(
              (entry) => !entry.isChecked || entry.isSelected,
            )}
            onClick={() => {
              moveCategories(true)
            }}
          >
            <IconArrowRight
              aria-hidden
              className="size-4 rotate-90 tablet:rotate-0"
            />
          </IconButton>
          <IconButton
            aria-label={intl.formatMessage({
              id: 'marketplacePartnerRegisterPage.categories.categoriesSelection.moveToAvailable.title',
            })}
            data-qa-id="sellerForm-button-moveToAvailable"
            disabled={entries.every(
              (entry) => !entry.isChecked || !entry.isSelected,
            )}
            onClick={() => {
              moveCategories(false)
            }}
          >
            <IconArrowLeft
              aria-hidden
              className="size-4 rotate-90 tablet:rotate-0"
            />
          </IconButton>
        </div>
        <MarketplacePartnerRegisterPageFormCategoriesSelectionCard
          data-qa-id="marketplacePartnerRegisterPage-card-selectedCategories"
          entries={selectedCategories.map(({ isChecked, key, label }) => ({
            isChecked,
            key,
            label,
          }))}
          id="marketplacePartnerRegisterPageCategoriesSelectionSelected"
          label={intl.formatMessage({
            id: 'marketplacePartnerRegisterPage.categoriesSelection.selected',
          })}
          onCheck={(value, checked) => {
            onCheck(value, checked, false)
          }}
        />
      </div>
      <div className="sr-only">
        <FormattedMessage id="marketplacePartnerRegisterPage.categories.title" />
      </div>
    </div>
  )
}
